import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUTMTracking } from '../../hooks/useUTMTracking';
import { supabase } from '../../utils/supabase';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d6rjpukddmmni7ck8u60';

type SubmitStatus = 'idle' | 'loading' | 'error';

export default function ContactPage() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [smsConsent, setSmsConsent] = useState(false);
  const { utmParams } = useUTMTracking();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setStatus('loading');
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value as string));

    // Add UTM params
    if (utmParams.traffic_source) body.append('traffic_source', utmParams.traffic_source);
    if (utmParams.utm_source) body.append('utm_source', utmParams.utm_source);
    if (utmParams.utm_medium) body.append('utm_medium', utmParams.utm_medium);
    if (utmParams.utm_campaign) body.append('utm_campaign', utmParams.utm_campaign);
    if (utmParams.utm_term) body.append('utm_term', utmParams.utm_term);
    if (utmParams.utm_content) body.append('utm_content', utmParams.utm_content);
    if (utmParams.referrer) body.append('referrer', utmParams.referrer);

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        const firstName = data.get('first_name') as string;
        const lastName = data.get('last_name') as string;
        const email = data.get('email') as string;
        const phone = data.get('phone') as string;

        // Fire-and-forget: Send email notification
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        fetch(`${supabaseUrl}/functions/v1/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'new_lead',
            lead: {
              name: `${firstName} ${lastName}`,
              email: email,
              phone: phone,
              service: 'Contact Form',
              message: message,
            },
          }),
        }).catch(() => {
          // Silent fail
        });

        // Insert lead into Supabase
        const followUpTime = new Date();
        followUpTime.setHours(followUpTime.getHours() + 24);

        supabase
          .from('leads')
          .insert({
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            notes: message,
            source: utmParams.traffic_source || 'Contact Form',
            stage: 'new',
            follow_up_scheduled_at: followUpTime.toISOString(),
            follow_up_sent: false,
          })
          .then(() => {
            // Silent success
          })
          .catch(() => {
            // Silent fail
          });

        // Redirect to thank you page
        navigate(`/thank-you?source=contact&name=${encodeURIComponent(firstName)}`);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';
    document.title = 'Contact Hoosier Life Insurance | Indianapolis Life Insurance Agent';
    const currentDate = new Date().toISOString();
    
    const insuranceAgencySchema = {
      '@context': 'https://schema.org',
      '@type': 'InsuranceAgency',
      '@id': `${baseUrl}/#insuranceagency`,
      name: 'Hoosier Life Insurance',
      image: `${baseUrl}/logo.png`,
      telephone: '+13176482983',
      email: 'info@hoosierlifeinsurance.com',
      url: `${baseUrl}/contact`,
      priceRange: 'Free Consultation',
      areaServed: [
        {
          '@type': 'City',
          name: 'Indianapolis',
          '@id': 'https://en.wikipedia.org/wiki/Indianapolis'
        },
        {
          '@type': 'City',
          name: 'Carmel',
          '@id': 'https://en.wikipedia.org/wiki/Carmel,_Indiana'
        },
        {
          '@type': 'City',
          name: 'Fishers',
          '@id': 'https://en.wikipedia.org/wiki/Fishers,_Indiana'
        },
        {
          '@type': 'City',
          name: 'Noblesville',
          '@id': 'https://en.wikipedia.org/wiki/Noblesville,_Indiana'
        },
        {
          '@type': 'City',
          name: 'Greenwood',
          '@id': 'https://en.wikipedia.org/wiki/Greenwood,_Indiana'
        },
        {
          '@type': 'City',
          name: 'Westfield',
          '@id': 'https://en.wikipedia.org/wiki/Westfield,_Indiana'
        },
        {
          '@type': 'City',
          name: 'Avon',
          '@id': 'https://en.wikipedia.org/wiki/Avon,_Indiana'
        },
        {
          '@type': 'City',
          name: 'Brownsburg',
          '@id': 'https://en.wikipedia.org/wiki/Brownsburg,_Indiana'
        },
        {
          '@type': 'State',
          name: 'Indiana',
          '@id': 'https://en.wikipedia.org/wiki/Indiana'
        }
      ],
      serviceType: [
        'Term Life Insurance',
        'Whole Life Insurance',
        'Final Expense Insurance',
        'Mortgage Protection Insurance',
        'Annuities',
        'Universal Life Insurance',
        'Indexed Universal Life Insurance'
      ],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '17:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '08:00',
          closes: '13:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Sunday',
          opens: '00:00',
          closes: '00:00',
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${baseUrl}/contact#webpage`,
      url: `${baseUrl}/contact`,
      name: 'Contact Hoosier Life Insurance | Indianapolis Life Insurance Agent',
      description: 'Get in touch with Hoosier Life Insurance for a free consultation. Call (317) 648-2983, email us, or book an appointment online. Serving Indianapolis and Central Indiana.',
      inLanguage: 'en-US',
      isPartOf: {
        '@id': `${baseUrl}/#website`
      },
      about: {
        '@id': `${baseUrl}/#organization`
      },
      datePublished: '2024-01-01T00:00:00+00:00',
      dateModified: currentDate,
      breadcrumb: {
        '@id': `${baseUrl}/contact#breadcrumb`
      }
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${baseUrl}/contact#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: baseUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Contact',
          item: `${baseUrl}/contact`
        }
      ]
    };

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        insuranceAgencySchema,
        webPageSchema,
        breadcrumbSchema
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'contact-page-schema';
    document.head.appendChild(script);

    // last-modified meta tag
    let lastModified = document.querySelector('meta[name="last-modified"]') as HTMLMetaElement | null;
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', currentDate);

    // canonical tag
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${baseUrl}/contact`);

    return () => {
      const existingScript = document.getElementById('contact-page-schema');
      if (existingScript) existingScript.remove();
      const lm = document.querySelector('meta[name="last-modified"]');
      if (lm) lm.remove();
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-[#1A3A52] text-white pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Get in Touch with Hoosier Life Insurance
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-10">
              We're here to answer your questions and help you find the right coverage for your family. Reach out today for a free consultation.
            </p>
            {/* Online Appointment Banner — inside hero for clean overlap-free layout */}
            <div className="max-w-3xl mx-auto">
              <Link
                to="/book-appointment"
                className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#D97642] hover:bg-[#C26638] text-white rounded-xl shadow-xl transition-all duration-300 p-5 sm:p-6"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-2xl"></i>
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl font-bold mb-0.5">Book Your Free Appointment Online</h3>
                    <p className="text-white/90 text-sm">Phone, video, or in-person — evening slots available until 9 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 font-semibold whitespace-nowrap text-sm bg-white/20 px-4 py-2 rounded-lg">
                  <span>Book Now</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-[#F9F7F4] rounded-xl p-6 text-center border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-fill text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Call Us</h3>
              <a href="tel:+13176482983" className="text-orange-500 hover:text-orange-600 font-semibold text-lg cursor-pointer block">
                (317) 648-2983
              </a>
              <p className="text-sm text-gray-600 mt-2">
                Mon–Fri: 9AM – 5PM<br />Sat: 8AM – 1PM
              </p>
              <p className="text-xs text-emerald-600 font-semibold mt-1 flex items-center justify-center gap-1">
                <i className="ri-video-chat-line"></i>
                Evening video appts until 9 PM
              </p>
            </div>

            <div className="bg-[#F9F7F4] rounded-xl p-6 text-center border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-fill text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Email Us</h3>
              <a href="mailto:info@hoosierlifeinsurance.com" className="text-orange-500 hover:text-orange-600 font-semibold cursor-pointer break-all text-sm">
                info@hoosierlifeinsurance.com
              </a>
              <p className="text-sm text-gray-600 mt-2">We'll respond within 24 hours</p>
            </div>

            <div className="bg-[#F9F7F4] rounded-xl p-6 text-center border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-service-line text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">How We Meet</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p className="flex items-center justify-center gap-2">
                  <i className="ri-phone-line text-orange-500"></i>
                  Phone Consultation
                </p>
                <p className="flex items-center justify-center gap-2">
                  <i className="ri-video-line text-orange-500"></i>
                  Video Conference
                </p>
                <p className="flex items-center justify-center gap-2">
                  <i className="ri-home-heart-line text-orange-500"></i>
                  In-Home Presentation
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-2">95% done remotely</p>
            </div>
          </div>

          {/* Google Reviews */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-orange-100 rounded-full px-4 py-1.5 mb-3">
              <i className="ri-star-fill text-orange-500 text-sm"></i>
              <span className="text-orange-600 text-sm font-semibold">Trusted by Indiana Families</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">What Our Clients Say</h2>
            <div className="flex items-center justify-center gap-3 text-sm text-gray-600">
              <span className="font-semibold">127 Google Reviews</span>
              <span className="text-gray-400">•</span>
              <div className="flex items-center gap-1">
                <span className="font-bold text-orange-500">4.9</span>
                <div className="flex">
                  {[1,2,3,4,5].map(i => <i key={i} className="ri-star-fill text-orange-400 text-xs"></i>)}
                </div>
              </div>
              <span className="text-gray-400">•</span>
              <span>Licensed Indiana Agent</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { name: 'Sarah M.', city: 'Indianapolis, IN', text: '"Outstanding service! They took the time to explain all my options and found me the perfect policy for my family. Highly recommend!"' },
              { name: 'Michael T.', city: 'Carmel, IN', text: '"Professional, knowledgeable, and genuinely cares about their clients. Made the whole process easy and stress-free."' },
              { name: 'Jennifer L.', city: 'Fishers, IN', text: '"Best insurance experience I\'ve ever had. They answered all my questions and got me covered quickly. Five stars!"' },
              { name: 'David R.', city: 'Noblesville, IN', text: '"Local, trustworthy, and always available when I need them. Couldn\'t ask for better service from an insurance agent."' },
            ].map((review) => (
              <div key={review.name} className="bg-[#F9F7F4] rounded-xl p-5 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => <i key={i} className="ri-star-fill text-orange-400 text-sm"></i>)}
                  </div>
                  <img src="https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_74x24dp.png" alt="Google" className="h-4 ml-auto" width={74} height={24} loading="lazy" decoding="async" />
                </div>
                <p className="text-sm text-gray-700 mb-3 leading-relaxed">{review.text}</p>
                <div className="text-xs font-semibold text-gray-900">{review.name}</div>
                <div className="text-xs text-gray-500">{review.city}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form + Map Section */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-white rounded-xl shadow-md p-8 space-y-5"
              >
                {/* Hidden UTM fields */}
                <input type="hidden" name="traffic_source" value={utmParams.traffic_source || ''} />
                <input type="hidden" name="utm_source" value={utmParams.utm_source || ''} />
                <input type="hidden" name="utm_medium" value={utmParams.utm_medium || ''} />
                <input type="hidden" name="utm_campaign" value={utmParams.utm_campaign || ''} />
                <input type="hidden" name="utm_term" value={utmParams.utm_term || ''} />
                <input type="hidden" name="utm_content" value={utmParams.utm_content || ''} />
                <input type="hidden" name="referrer" value={utmParams.referrer || ''} />

                {/* Name row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="Jane"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Smith"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="jane@example.com"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="(317) 000-0000"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1.5">
                    Your Message *
                    <span className="ml-2 text-xs font-normal text-gray-500">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    placeholder="Tell us how we can help you..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500 flex items-center gap-1.5">
                    <i className="ri-error-warning-line text-base"></i>
                    Something went wrong. Please try again or call us at (317) 648-2983.
                  </p>
                )}

                {/* SMS Consent Checkbox */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="sms_consent"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      required
                      className="mt-0.5 w-4 h-4 accent-[#D97642] shrink-0 cursor-pointer"
                    />
                    <span className="text-xs text-gray-700 leading-relaxed">
                      <strong className="text-gray-900">I agree to receive SMS/text messages</strong> from Hoosier Life Insurance at the phone number provided. Message frequency varies. Message &amp; data rates may apply. Reply <strong>STOP</strong> to opt out at any time. Reply <strong>HELP</strong> for help. Consent is not a condition of purchase. See our{' '}
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#D97642] underline hover:text-[#C26638]">Privacy Policy</a> for full details.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading' || !smsConsent}
                  className="w-full py-3.5 bg-[#D97642] text-white text-base font-semibold rounded-lg hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-base"></i>
                      Send Message
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center leading-relaxed">
                  By submitting, you agree to be contacted by Hoosier Life Insurance. We never sell your information.
                </p>

                {/* Chat escape hatch */}
                <div className="flex items-center gap-3 pt-1">
                  <div className="flex-1 h-px bg-gray-100"></div>
                  <span className="text-[11px] text-gray-400 uppercase tracking-wide shrink-0">or</span>
                  <div className="flex-1 h-px bg-gray-100"></div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const btn = document.querySelector<HTMLElement>('[data-readdy-chat-button]') ||
                      document.querySelector<HTMLElement>('.readdy-chat-button') ||
                      document.querySelector<HTMLElement>('button[class*="readdy"]');
                    if (btn) {
                      btn.click();
                    } else {
                      (window as unknown as Record<string, unknown>).openReaddyChat?.();
                    }
                  }}
                  className="w-full py-2.5 border border-[#D97642]/40 text-[#D97642] text-[13px] font-medium rounded-lg hover:bg-[#D97642]/5 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <span className="relative flex w-2 h-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D97642] opacity-60"></span>
                    <span className="relative inline-flex rounded-full w-2 h-2 bg-[#D97642]"></span>
                  </span>
                  Prefer to chat? Ask our AI — no form needed
                </button>
              </form>
            </div>

            {/* Right: Map + Book CTA */}
            <div className="space-y-6">
              {/* Service Area Card - Replaces Map */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="bg-gradient-to-br from-[#1A3A52] to-[#0f2044] p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-lg">
                      <i className="ri-map-2-line text-orange-400 text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Central Indiana Coverage</h3>
                      <p className="text-white/70 text-sm">Serving 25+ cities across 8 counties</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {[
                      { icon: 'ri-building-2-line', label: 'Indianapolis Metro', highlight: 'Downtown & Suburbs' },
                      { icon: 'ri-home-4-line', label: 'Hamilton County', highlight: 'Carmel, Fishers, Noblesville' },
                      { icon: 'ri-community-line', label: 'Hendricks County', highlight: 'Avon, Brownsburg, Plainfield' },
                      { icon: 'ri-road-map-line', label: 'Johnson County', highlight: 'Greenwood, Franklin' },
                    ].map((area) => (
                      <div key={area.label} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                        <div className="flex items-start gap-2 mb-1">
                          <i className={`${area.icon} text-orange-400 text-lg mt-0.5`}></i>
                          <div>
                            <div className="text-white font-semibold text-sm leading-tight">{area.label}</div>
                            <div className="text-white/60 text-xs mt-0.5">{area.highlight}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="flex items-start gap-2 mb-2">
                      <i className="ri-map-pin-range-line text-orange-400 text-lg mt-0.5"></i>
                      <div>
                        <h4 className="text-white font-semibold text-sm mb-1">Key ZIP Codes We Serve</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {['46032','46033','46037','46038','46060','46123','46142','46143','46168','46201','46220','46240','46250','46260','46280'].map(zip => (
                            <span key={zip} className="text-xs bg-white/20 text-white px-2 py-0.5 rounded font-mono">{zip}</span>
                          ))}
                          <span className="text-xs text-orange-300 font-semibold px-2 py-0.5">+60 more</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white border-t border-gray-100">
                  <div className="flex items-start gap-2 mb-3 bg-orange-50 rounded-lg px-3 py-2.5 border border-orange-100">
                    <i className="ri-home-heart-line text-[#D97642] text-[16px] mt-0.5 shrink-0"></i>
                    <p className="text-[12px] text-gray-600 leading-relaxed">
                      <strong className="text-[#1A3A52]">We come to you</strong> — consultations available by phone, video, or in-home appointment throughout Central Indiana
                    </p>
                  </div>
                  <a
                    href="https://www.google.com/maps/search/life+insurance+Indianapolis+IN"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-[#0f2044] hover:bg-[#1a3060] text-white font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-map-2-line text-orange-400"></i>
                    View Full Service Area
                    <i className="ri-external-link-line text-sm opacity-70"></i>
                  </a>
                  <p className="text-xs text-gray-500 text-center mt-2">Serving 25+ cities across Central Indiana</p>
                </div>
              </div>

              {/* Book Appointment CTA */}
              <div className="bg-[#1A3A52] rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-2">Ready to Get Started?</h3>
                <p className="text-white/80 text-sm mb-4">Book a free consultation — phone, video, or in-person. Evening video slots available until 9 PM.</p>
                <Link
                  to="/book-appointment"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] hover:bg-[#C26638] text-white font-bold rounded-lg transition-colors whitespace-nowrap w-full justify-center"
                >
                  <i className="ri-calendar-check-line"></i>
                  Book Free Appointment
                </Link>
              </div>

              {/* Service Areas */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-map-2-line text-orange-500"></i>
                  Service Areas
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['Indianapolis', 'Carmel', 'Fishers', 'Noblesville', 'Greenwood', 'Avon', 'Brownsburg', 'Westfield'].map(city => (
                    <span key={city} className="text-xs bg-orange-50 text-orange-700 border border-orange-100 px-2.5 py-1 rounded-full font-medium">{city}</span>
                  ))}
                </div>
                <Link to="/service-areas" className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors">
                  View all service areas <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area ZIP Codes */}
      <section className="py-16 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/30 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-map-2-line text-orange-400 text-sm"></i>
              <span className="text-orange-300 text-sm font-medium">Local Coverage</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Service Areas &amp; ZIP Codes We Cover</h2>
            <p className="text-blue-200/70 max-w-2xl mx-auto text-sm">
              Hoosier Life Insurance proudly serves Indianapolis and the surrounding Central Indiana communities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-building-2-line', title: 'Indianapolis', zips: ['46201','46202','46203','46204','46205','46208','46218','46220','46226','46228','46240','46250','46256','46260','46268'] },
              { icon: 'ri-home-4-line', title: 'Carmel & Fishers', zips: ['46032','46033','46037','46038','46040','46055','46280','46290'] },
              { icon: 'ri-map-pin-2-line', title: 'Noblesville & Hamilton Co.', zips: ['46060','46061','46062','46064','46069','46074','46077'] },
              { icon: 'ri-community-line', title: 'Greenwood & Johnson Co.', zips: ['46142','46143','46184','46227','46237','46259'] },
              { icon: 'ri-road-map-line', title: 'Avon & Hendricks Co.', zips: ['46123','46168','46231','46234','46278'] },
              { icon: 'ri-pin-distance-line', title: 'Lawrence & Beech Grove', zips: ['46107','46163','46216','46219','46229','46235','46236','46239'] },
            ].map((group) => (
              <div key={group.title} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-orange-500/20 rounded-lg">
                    <i className={`${group.icon} text-orange-400`}></i>
                  </div>
                  <h3 className="text-white font-bold text-base">{group.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.zips.map(zip => (
                    <span key={zip} className="text-xs bg-white/10 text-blue-200 px-2 py-1 rounded font-mono">{zip}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-blue-200/60 text-sm mb-4">Don't see your ZIP code? We likely still serve your area — give us a call!</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors whitespace-nowrap">
                <i className="ri-phone-fill"></i>
                Call (317) 648-2983
              </a>
              <Link to="/service-areas" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-lg transition-colors whitespace-nowrap">
                <i className="ri-map-2-line"></i>
                View All Service Areas
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}